import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Clock, Menu, X, Bell, MessageCircle, Sun, Moon, User, LogOut, LayoutDashboard, ChevronDown, Sparkles } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useWebSocket } from '../../context/WebSocketContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { AnimatedCredits } from '../common/Animations';

export const Header = () => {
  const { user, isAuthenticated, logout, api } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { subscribe } = useWebSocket();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [previousCredits, setPreviousCredits] = useState(user?.credits_balance);
  const [scrolled, setScrolled] = useState(false);
  const [themeRotating, setThemeRotating] = useState(false);
  const [notificationPulse, setNotificationPulse] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchNotificationCount();
      fetchUnreadMessages();
    }
  }, [isAuthenticated]);

  // Subscribe to real-time notifications
  useEffect(() => {
    if (!isAuthenticated) return;

    const unsubscribe = subscribe('notification', () => {
      setUnreadNotifications((prev) => prev + 1);
      setNotificationPulse(true);
      setTimeout(() => setNotificationPulse(false), 2000);
    });

    const unsubscribeMsg = subscribe('new_message', () => {
      setUnreadMessages((prev) => prev + 1);
    });

    return () => {
      unsubscribe();
      unsubscribeMsg();
    };
  }, [isAuthenticated, subscribe]);

  // Track credit changes
  useEffect(() => {
    if (user?.credits_balance !== previousCredits && previousCredits !== undefined) {
      setPreviousCredits(user?.credits_balance);
    }
  }, [user?.credits_balance, previousCredits]);

  const fetchNotificationCount = async () => {
    try {
      const response = await api.get('/notifications/count');
      setUnreadNotifications(response.data.count);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const fetchUnreadMessages = async () => {
    try {
      const response = await api.get('/conversations');
      const total = response.data.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
      setUnreadMessages(total);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const handleThemeToggle = () => {
    setThemeRotating(true);
    toggleTheme();
    setTimeout(() => setThemeRotating(false), 500);
  };

  const navLinks = [
    { href: '/annonces', label: 'Offres & Besoins' },
    { href: '/services', label: 'Services' },
    { href: '/activity', label: 'Activité' },
    { href: '/how-it-works', label: 'Comment ça marche' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header 
      className={`sticky top-0 z-50 transition-all duration-500 ${
        scrolled 
          ? 'glass shadow-lg' 
          : 'bg-background/60 backdrop-blur-md'
      }`}
      data-testid="header"
    >
      <div className="container-app">
        <nav className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group"
            data-testid="logo-link"
          >
            <div className="relative">
              <Clock className="w-8 h-8 text-primary transition-all duration-300 group-hover:rotate-12 group-hover:scale-110" />
              <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
            <span className="text-xl md:text-2xl font-bold tracking-tight">
              Okn<span className="text-gradient">î</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`relative px-4 py-2 rounded-full font-medium transition-all duration-300 ${
                  isActive(link.href)
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`nav-${link.href.slice(1)}`}
              >
                {link.label}
                {isActive(link.href) && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary rounded-full" />
                )}
              </Link>
            ))}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* Theme Toggle - Animated */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleThemeToggle}
              className="rounded-full relative overflow-hidden"
              data-testid="theme-toggle"
            >
              <div className={`theme-toggle-icon ${themeRotating ? 'rotating' : ''}`}>
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5 text-secondary" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </div>
            </Button>

            {isAuthenticated ? (
              <>
                {/* Credits Badge - Animated */}
                <Link
                  to="/dashboard"
                  className="hidden sm:flex credit-badge-glow group"
                  data-testid="credits-badge"
                >
                  <Clock className="w-4 h-4 text-secondary transition-transform group-hover:rotate-12" />
                  <AnimatedCredits 
                    value={user?.credits_balance || 0} 
                    previousValue={previousCredits}
                  />
                </Link>

                {/* Notifications - With pulse animation */}
                <Link 
                  to="/notifications" 
                  className={`relative ${notificationPulse ? 'notification-pulse' : ''}`} 
                  data-testid="notifications-link"
                >
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <Bell className={`w-5 h-5 transition-transform ${notificationPulse ? 'animate-wiggle' : ''}`} />
                    {unreadNotifications > 0 && (
                      <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-destructive animate-scale-in">
                        {unreadNotifications > 9 ? '9+' : unreadNotifications}
                      </Badge>
                    )}
                  </Button>
                </Link>

                {/* Messages */}
                <Link to="/messages" className="relative" data-testid="messages-link">
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <MessageCircle className="w-5 h-5" />
                    {unreadMessages > 0 && (
                      <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-destructive animate-scale-in">
                        {unreadMessages > 9 ? '9+' : unreadMessages}
                      </Badge>
                    )}
                  </Button>
                </Link>

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="flex items-center gap-2 rounded-full pl-1 pr-3 hover:bg-muted/80"
                      data-testid="user-menu-trigger"
                    >
                      <div className={`relative ${user?.verified ? 'avatar-verified' : ''}`}>
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user?.avatar_url} alt={user?.full_name} />
                          <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                            {user?.full_name?.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 rounded-xl">
                    <div className="px-3 py-3">
                      <p className="font-semibold truncate">{user?.full_name}</p>
                      <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <div className="credit-badge text-xs">
                          <Clock className="w-3 h-3" />
                          {user?.credits_balance}
                        </div>
                        {user?.total_exchanges > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {user?.total_exchanges} échanges
                          </Badge>
                        )}
                      </div>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer">
                        <LayoutDashboard className="w-4 h-4" />
                        Tableau de bord
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
                        <User className="w-4 h-4" />
                        Mon profil
                      </Link>
                    </DropdownMenuItem>
                    {user?.is_admin && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2 cursor-pointer">
                          <Sparkles className="w-4 h-4" />
                          Administration
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="flex items-center gap-2 cursor-pointer text-destructive focus:text-destructive focus:bg-destructive/10"
                      data-testid="logout-btn"
                    >
                      <LogOut className="w-4 h-4" />
                      Déconnexion
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/login" className="hidden sm:block">
                  <Button variant="ghost" className="rounded-full" data-testid="login-btn">
                    Connexion
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button className="btn-primary group" data-testid="signup-btn">
                    <span className="hidden sm:inline">S'inscrire</span>
                    <span className="sm:hidden">Rejoindre</span>
                    <Sparkles className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Button>
                </Link>
              </>
            )}

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden rounded-full"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              <div className="relative w-6 h-6">
                <span className={`absolute left-0 top-1 w-6 h-0.5 bg-current transition-all duration-300 ${mobileMenuOpen ? 'rotate-45 top-3' : ''}`} />
                <span className={`absolute left-0 top-3 w-6 h-0.5 bg-current transition-all duration-300 ${mobileMenuOpen ? 'opacity-0' : ''}`} />
                <span className={`absolute left-0 top-5 w-6 h-0.5 bg-current transition-all duration-300 ${mobileMenuOpen ? '-rotate-45 top-3' : ''}`} />
              </div>
            </Button>
          </div>
        </nav>

        {/* Mobile Menu - With slide animation */}
        <div 
          className={`md:hidden overflow-hidden transition-all duration-300 ease-out ${
            mobileMenuOpen ? 'max-h-96 pb-4' : 'max-h-0'
          }`}
          data-testid="mobile-menu"
        >
          <div className="flex flex-col gap-1 pt-2">
            {navLinks.map((link, index) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-3 rounded-xl font-medium transition-all duration-300 ${
                  isActive(link.href)
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
                style={{ 
                  opacity: mobileMenuOpen ? 1 : 0,
                  transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(-20px)',
                  transitionDelay: `${index * 50}ms`
                }}
              >
                {link.label}
              </Link>
            ))}
            {!isAuthenticated && (
              <Link
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 rounded-xl font-medium text-muted-foreground hover:bg-muted transition-colors"
                style={{ 
                  opacity: mobileMenuOpen ? 1 : 0,
                  transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(-20px)',
                  transitionDelay: `${navLinks.length * 50}ms`
                }}
              >
                Connexion
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
